import { useState } from "react";

function LanguageGlobeIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 40 40" className={className} fill="none" aria-hidden="true">
      <circle cx="20" cy="20" r="15.8" fill="currentColor" opacity="0.1" />
      <circle cx="20" cy="20" r="14.4" stroke="currentColor" strokeWidth="1.75" />
      <path
        d="M20 5.8c4.15 3.76 6.24 8.5 6.24 14.2S24.15 30.44 20 34.2C15.85 30.44 13.76 25.7 13.76 20S15.85 9.56 20 5.8Z"
        stroke="currentColor"
        strokeWidth="1.35"
        strokeLinejoin="round"
      />
      <path
        d="M7.6 15.4h24.8M7.6 24.6h24.8"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinecap="round"
        opacity="0.86"
      />
      <path
        d="M6.1 20h27.8M20 5.8v28.4"
        stroke="currentColor"
        strokeWidth="1.08"
        strokeLinecap="round"
        opacity="0.62"
      />
      <path
        d="M11.1 10.85c2.65 1.42 5.62 2.14 8.9 2.14s6.25-.72 8.9-2.14M11.1 29.15c2.65-1.42 5.62-2.14 8.9-2.14s6.25.72 8.9 2.14"
        stroke="currentColor"
        strokeWidth="1.08"
        strokeLinecap="round"
        opacity="0.7"
      />
      <circle cx="20" cy="20" r="3" fill="currentColor" opacity="0.2" />
    </svg>
  );
}

function normalizeOption(option) {
  if (Array.isArray(option)) {
    return { code: option[0], label: option[1] };
  }
  return option;
}

export default function PublicLanguageSwitcher({
  options,
  activeCode,
  onChange,
  className = "",
  ariaLabel = "Select language",
  mobileMenuAlign = "right",
}) {
  const [open, setOpen] = useState(false);
  const normalizedOptions = options.map(normalizeOption);
  const activeOption = normalizedOptions.find((option) => option.code === activeCode) || normalizedOptions[0];
  const handleChange = (option) => {
    setOpen(false);
    onChange(option);
  };

  return (
    <div className={`relative inline-flex max-w-full ${className}`} aria-label={ariaLabel}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="inline-flex h-9 max-w-[132px] items-center gap-1.5 rounded-full border border-[#c8a45d]/30 bg-black/56 pl-2.5 pr-2 text-[11px] font-semibold tracking-[0.04em] text-white/82 shadow-[0_10px_28px_rgba(0,0,0,0.28),inset_0_1px_0_rgba(255,255,255,0.06)] backdrop-blur-xl transition hover:border-[#d7b76b]/62 hover:text-[#f2d58d] sm:hidden"
      >
        <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-[#c8a45d]/26 bg-[#d7b76b]/8 text-[#d7b76b]">
          <LanguageGlobeIcon className="h-3.5 w-3.5 drop-shadow-[0_0_12px_rgba(215,183,107,0.34)]" />
        </span>
        <span className="min-w-0 truncate">{activeOption?.label || "LANG"}</span>
        <span className={`ml-auto shrink-0 text-[10px] text-[#d7b76b]/78 transition ${open ? "rotate-180" : ""}`}>⌄</span>
      </button>

      {open && (
        <div
          role="listbox"
          data-testid="public-language-mobile-menu"
          className={`absolute top-[calc(100%+8px)] z-[98] w-[176px] max-w-[calc(100vw-32px)] overflow-hidden rounded-[14px] border border-[#c8a45d]/42 bg-[#070604]/96 p-1.5 text-left shadow-[0_28px_80px_rgba(0,0,0,0.56),inset_0_1px_0_rgba(255,255,255,0.08)] backdrop-blur-2xl sm:hidden ${
            mobileMenuAlign === "left" ? "left-0 origin-top-left" : "right-0 origin-top-right"
          }`}
        >
          {normalizedOptions.map((option) => {
            const active = option.code === activeCode;
            return (
              <button
                key={option.code}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => handleChange(option)}
                className={`group flex w-full items-center gap-3 rounded-[10px] px-3 py-2.5 text-left transition ${
                  active
                    ? "bg-[#d7b76b]/16 text-[#f4d98d]"
                    : "text-white/78 hover:bg-white/[0.06] hover:text-white"
                }`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${active ? "bg-[#e8c36e]" : "bg-white/24 group-hover:bg-[#d7b76b]/70"}`} />
                <span className="text-[13px] font-semibold tracking-[0.05em]">{option.label}</span>
              </button>
            );
          })}
        </div>
      )}

      <div className="hidden max-w-full items-center gap-1 rounded-full border border-[#c8a45d]/26 bg-black/50 p-1 text-[10px] font-semibold tracking-[0.08em] text-white/60 shadow-[0_10px_28px_rgba(0,0,0,0.24),inset_0_1px_0_rgba(255,255,255,0.06)] backdrop-blur-xl sm:inline-flex">
        <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#c8a45d]/24 bg-[#d7b76b]/8 text-[#d7b76b]">
          <LanguageGlobeIcon className="h-[18px] w-[18px] drop-shadow-[0_0_12px_rgba(215,183,107,0.34)]" />
        </span>
        <span className="inline-flex min-w-0 flex-wrap items-center gap-1">
          {normalizedOptions.map((option) => {
            const active = option.code === activeCode;
            return (
              <button
                key={option.code}
                type="button"
                onClick={() => handleChange(option)}
                className={`h-8 rounded-full px-2.5 transition duration-300 ${
                  active
                    ? "bg-[#d7b76b] text-black shadow-[0_10px_24px_rgba(215,183,107,0.22)]"
                    : "text-white/56 hover:bg-white/8 hover:text-[#f2d58d]"
                }`}
              >
                {option.label}
              </button>
            );
          })}
        </span>
      </div>
    </div>
  );
}
